import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, Heart, LogIn, LogOut, Package, ShieldCheck, Ticket, UserRound, UserRoundPlus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import styles from './UserMenu.module.css';

const customerLinks = [
  { label: 'Mon profil', description: 'Vos informations personnelles', href: '/profile', icon: UserRound },
  { label: 'Mes commandes', description: 'Suivre vos achats CAB', href: '/orders', icon: Package },
  { label: 'Mes favoris', description: 'Votre sélection enregistrée', href: '/wishlist', icon: Heart },
  { label: 'Mon abonnement', description: 'Accéder à votre carte membre', href: '/membership/card', icon: Ticket },
];

const guestLinks = [
  { label: 'Se connecter', description: 'Retrouver votre espace client', href: '/login', icon: LogIn },
  { label: 'Créer un compte', description: 'Rejoindre la communauté CAB', href: '/register', icon: UserRoundPlus },
];

const UserMenu = ({ user, onSignOut }) => {
  const menuId = useId();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(() => window.matchMedia('(max-width: 639px)').matches);
  const [position, setPosition] = useState({ left: 12, top: 12 });
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const reducedMotion = useReducedMotion();
  const name = user?.name || user?.email?.split('@')[0] || 'Votre compte';
  const initials = user ? name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'CAB' : 'CAB';
  const links = user ? customerLinks : guestLinks;

  useEffect(() => {
    const query = window.matchMedia('(max-width: 639px)');
    const updateCompact = () => setCompact(query.matches);
    updateCompact();
    query.addEventListener('change', updateCompact);
    return () => query.removeEventListener('change', updateCompact);
  }, []);

  useLayoutEffect(() => {
    if (!open || compact) return undefined;
    const placePanel = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const triggerRect = trigger.getBoundingClientRect();
      const width = Math.min(panel.offsetWidth, window.innerWidth - 24);
      const left = Math.min(Math.max(12, triggerRect.right - width), window.innerWidth - width - 12);
      const below = triggerRect.bottom + 10;
      const top = below + panel.offsetHeight <= window.innerHeight - 12
        ? below
        : Math.max(12, triggerRect.top - panel.offsetHeight - 10);
      setPosition({ left, top });
    };
    placePanel();
    window.addEventListener('resize', placePanel);
    window.addEventListener('scroll', placePanel, true);
    return () => {
      window.removeEventListener('resize', placePanel);
      window.removeEventListener('scroll', placePanel, true);
    };
  }, [open, compact]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.documentElement.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus({ preventScroll: true });
      }
    };
    if (compact) document.documentElement.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.documentElement.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [open, compact]);

  const close = (returnFocus = false) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus({ preventScroll: true });
  };

  const handleSignOut = async () => {
    if (!onSignOut || signingOut) return;
    setSigningOut(true);
    try {
      await onSignOut();
      close(false);
    } finally {
      setSigningOut(false);
    }
  };

  const menu = (
    <AnimatePresence>
      {open && (
        <motion.div
          className={styles.layer}
          data-compact={compact || undefined}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0.1 : 0.18 }}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) close(true);
          }}
        >
          <motion.section
            ref={panelRef}
            id={menuId}
            className={styles.panel}
            role={compact ? 'dialog' : 'menu'}
            aria-modal={compact || undefined}
            aria-label="Menu du compte CAB"
            style={!compact ? { left: position.left, top: position.top } : undefined}
            initial={compact ? { y: '100%' } : { opacity: 0, scale: 0.96, y: -5 }}
            animate={compact ? { y: 0 } : { opacity: 1, scale: 1, y: 0 }}
            exit={compact ? { y: '100%' } : { opacity: 0, scale: 0.98, y: -4 }}
            transition={reducedMotion ? { duration: 0.12 } : { duration: compact ? 0.3 : 0.2, ease: [0.22, 1, 0.36, 1] }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {compact && <span className={styles.handle} aria-hidden="true" />}
            <header className={styles.header}>
              <div className={styles.eyebrow}><ShieldCheck size={13} /> CAB STORE / ESPACE CLIENT</div>
              <button type="button" className={styles.close} onClick={() => close(true)} aria-label="Fermer le menu"><X size={17} /></button>
            </header>

            <div className={styles.identity}>
              {user?.avatarSrc && !avatarFailed
                ? <img className={styles.avatar} src={user.avatarSrc} alt="" onError={() => setAvatarFailed(true)} />
                : <span className={styles.avatarFallback}>{initials}</span>}
              <div className={styles.identityCopy}>
                <strong>{name}</strong>
                <span>{user?.email || 'Bienvenue au CAB Store'}</span>
              </div>
              {user && <span className={styles.memberMark} aria-label="Compte CAB"><ShieldCheck size={15} /></span>}
            </div>

            <nav className={styles.links} aria-label="Actions du compte">
              {links.map(({ label, description, href, icon: Icon }) => (
                <Link key={href} to={href} role="menuitem" className={styles.link} onClick={() => close(false)}>
                  <span className={styles.linkIcon}><Icon size={17} strokeWidth={1.7} /></span>
                  <span className={styles.linkCopy}><strong>{label}</strong><small>{description}</small></span>
                  <ArrowUpRight className={styles.linkArrow} size={15} />
                </Link>
              ))}
            </nav>

            {user && <button type="button" role="menuitem" className={styles.signOut} onClick={handleSignOut} disabled={signingOut}>
              <LogOut size={16} strokeWidth={1.7} />
              <span>{signingOut ? 'Déconnexion…' : 'Se déconnecter'}</span>
              {signingOut && <span className={styles.spinner} aria-hidden="true" />}
            </button>}
            <footer className={styles.footer}><span>CLUB ATHLÉTIQUE BIZERTIN</span><span>EST. 1928</span></footer>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return <>
    <button
      ref={triggerRef}
      type="button"
      className={`cab-nav__icon-button ${styles.trigger}`}
      onClick={() => setOpen((value) => !value)}
      aria-label={user ? `Ouvrir le menu du compte, ${name}` : 'Se connecter ou créer un compte'}
      aria-haspopup={compact ? 'dialog' : 'menu'}
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
    >
      {user?.avatarSrc && !avatarFailed
        ? <img src={user.avatarSrc} alt="" onError={() => setAvatarFailed(true)} />
        : <UserRound size={18} strokeWidth={1.7} />}
      {user && <span className={styles.triggerDot} aria-hidden="true" />}
    </button>
    {createPortal(menu, document.body)}
  </>;
};

export default UserMenu;